import type { BasketItem } from '../types/order';

/**
 * Ένωση ίδιων γραμμών στο καλάθι Take Away / Delivery: δεύτερο πάτημα στο ίδιο
 * προϊόν ανεβάζει την ποσότητα της υπάρχουσας γραμμής αντί να φτιάξει νέα «×1».
 * Ίδια συμπεριφορά με το dine-in (addOrMergeOrderItem) και το PDA.
 *
 * Ενώνονται ΜΟΝΟ τα ολόιδια: ίδιο προϊόν, ίδιο μέγεθος, ίδια τιμή μονάδας, και ΚΑΜΙΑ
 * από τις δύο γραμμές δεν έχει κάτι που αφορά το συγκεκριμένο τεμάχιο — αφαίρεση
 * υλικού, extra, επιλογή, σημείωση, έκπτωση, ζύγισμα, προωθητική ενέργεια, γραμμή
 * επανάληψης παραγγελίας ή τέλος. Ο κανόνας είναι ο ίδιος με του barcode
 * (utils/barcodeLine → findBarcodeMergeLine), συν προωθητική/επανάληψη/τέλη.
 *
 * Η τιμή μονάδας ελέγχεται σε ακέραια λεπτά: με ίδια τιμή η ένωση δεν αλλάζει ποτέ το
 * ποσό της παραγγελίας — μόνο το πώς φαίνεται στο καλάθι (μία γραμμή ×2 αντί για δύο ×1).
 */

const cents = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};

const unitPrice = (item: BasketItem) => item.price ?? item.totalPrice;

/** Η γραμμή δεν έχει τίποτα δικό της — μπορεί να ενωθεί με ολόιδια. */
export function isPlainBasketLine(item: BasketItem): boolean {
  return (
    !item.weight &&
    (!item.removedIngredients || item.removedIngredients.length === 0) &&
    (!item.addedExtras || item.addedExtras.length === 0) &&
    (!item.selected_options || item.selected_options.length === 0) &&
    !(item.specialInstructions && item.specialInstructions.trim()) &&
    !(item.notes && item.notes.trim()) &&
    !(Number(item.discount) > 0) &&
    !item.applied_promotion_id &&
    // Γραμμή από πρόταση πώλησης: κρατά τη δική της σήμανση (αναφορά των κεντρικών).
    !item.upsell_suggestion_id &&
    !item.fromReorder &&
    !item.keepHistoricalFee &&
    // Τα τέλη έχουν δικούς τους κανόνες ποσότητας (utils/autoFee).
    item.product?.fee_category == null
  );
}

const sizeIdOf = (item: BasketItem) => (item.selectedSize ? Number(item.selectedSize.id) : null);

/** Η υπάρχουσα γραμμή με την οποία ενώνεται η νέα — ή undefined για νέα γραμμή. */
export function findMergeLine(basket: BasketItem[], line: BasketItem): BasketItem | undefined {
  if (!isPlainBasketLine(line)) return undefined;
  const lineCents = cents(unitPrice(line));
  if (!Number.isFinite(lineCents)) return undefined;
  return basket.find(item =>
    Number(item.product.id) === Number(line.product.id) &&
    sizeIdOf(item) === sizeIdOf(line) &&
    cents(unitPrice(item)) === lineCents &&
    isPlainBasketLine(item)
  );
}

/** Το καλάθι μετά την προσθήκη της γραμμής: ένωση με ολόιδια, αλλιώς νέα γραμμή στο τέλος. */
export function addOrMergeBasketLine(basket: BasketItem[], line: BasketItem): BasketItem[] {
  const target = findMergeLine(basket, line);
  if (!target) return [...basket, line];
  return basket.map(item => (item.id === target.id ? { ...item, quantity: item.quantity + line.quantity } : item));
}
